import * as React from "react";
import moment from "moment";
import styled from "styled-components";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { getNiceAxisTicks } from "../utils/getNiceAxisTicks";

const Caption = styled.p`
  margin: 20px 0 0;
  text-align: center;
  color: #666666;
`;

interface DataPoint {
  date: number;
  value: number;
}

interface Props {
  data: [number, number][];
  color: string;
  yAxisTitle: string;
  caption?: string;
}

const formatDateTick = (value: number) => moment(value).format("D. MMM");

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #CCC",
        padding: "8px 12px",
        fontSize: 13,
      }}
    >
      <div>{moment(label).format("D MMMM YYYY")}</div>
      <div>{`${Math.round(payload[0].value as number)}% comfort`}</div>
    </div>
  );
};

export const ComfortTrendBarChart: React.FC<Props> = ({
  data,
  color,
  yAxisTitle,
  caption,
}) => {
  const chartData: DataPoint[] = data.map(([date, value]) => ({
    date,
    value,
  }));
  const tickInterval = Math.max(0, Math.ceil(chartData.length / 15) - 1);
  const yAxisTicks = getNiceAxisTicks(chartData.map((d) => d.value));

  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
        >
          <CartesianGrid vertical={false} stroke="#e6e6e6" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            interval={tickInterval}
            tick={{ fontSize: 13 }}
          />
          <YAxis
            domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
            ticks={yAxisTicks}
            tick={{ fontSize: 13 }}
            label={{
              value: yAxisTitle,
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 13, textAnchor: "middle" },
            }}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
          />
          <Bar dataKey="value" fill={color} />
        </BarChart>
      </ResponsiveContainer>
      {caption && <Caption>{caption}</Caption>}
    </div>
  );
};
